/**
 * Aspect Ratio Detection Utility
 * Handles automatic detection of media aspect ratios for video processing
 * Created: August 2025
 */

import { MediaItem } from '../types';

export type AspectRatioType = '16:9' | '9:16' | '1:1';

export interface AspectRatioResult {
  ratio: AspectRatioType;
  confidence: 'high' | 'medium' | 'low';
  dimensions?: { width: number; height: number };
  source: 'existing-element' | 'metadata' | 'fallback';
}

export interface DetectionOptions {
  preferExistingElements?: boolean;
  timeout?: number;
  fallbackRatio?: AspectRatioType;
}

/**
 * Determines aspect ratio type from numeric ratio
 */
const classifyAspectRatio = (ratio: number): AspectRatioType => {
  const VERTICAL_RATIO = 9 / 16; // 0.5625
  const SQUARE_RATIO = 1;
  const HORIZONTAL_RATIO = 16 / 9; // 1.777...
  const TOLERANCE = 0.1; // Increased tolerance for better classification

  console.log('🔍 [AspectRatio Classification] Input ratio:', ratio);
  
  // Vertical (portrait): ratio < 0.8
  if (ratio < 0.8) {
    console.log('🔍 [AspectRatio Classification] → 9:16 (Vertical/Portrait)');
    return '9:16';
  } 
  // Square: ratio between 0.8 and 1.2
  else if (ratio >= 0.8 && ratio <= 1.2) {
    console.log('🔍 [AspectRatio Classification] → 1:1 (Square)');
    return '1:1';
  } 
  // Horizontal (landscape): ratio > 1.2
  else {
    console.log('🔍 [AspectRatio Classification] → 16:9 (Horizontal/Landscape)');
    return '16:9';
  }
};

/**
 * Attempts to get aspect ratio from existing DOM elements
 */
const detectFromExistingElements = async (
  items: MediaItem[]
): Promise<AspectRatioResult | null> => {
  const firstVideoItem = items.find(item => item.type === 'video');
  const firstImageItem = items.find(item => item.type === 'image');

  // Check existing video elements
  if (firstVideoItem) {
    const existingVideo = document.querySelector('video');
    if (existingVideo && existingVideo.videoWidth > 0) {
      const ratio = existingVideo.videoWidth / existingVideo.videoHeight;
      return {
        ratio: classifyAspectRatio(ratio),
        confidence: 'high',
        dimensions: { 
          width: existingVideo.videoWidth, 
          height: existingVideo.videoHeight 
        },
        source: 'existing-element'
      };
    }
  }

  // Check existing image elements
  if (firstImageItem) {
    const existingImages = document.querySelectorAll('img');
    const itemFileName = firstImageItem.url.split('/').pop()?.split('?')[0] || '';
    
    for (let img of existingImages) {
      if (img.src.includes(itemFileName) && img.naturalWidth > 0) {
        const ratio = img.naturalWidth / img.naturalHeight;
        return {
          ratio: classifyAspectRatio(ratio),
          confidence: 'high',
          dimensions: { 
            width: img.naturalWidth, 
            height: img.naturalHeight 
          },
          source: 'existing-element'
        };
      }
    }
  }

  return null;
};

/**
 * Detects aspect ratio by loading media metadata
 */
const detectFromMetadata = async (
  items: MediaItem[],
  timeout: number = 10000 // Increased from 5000ms to 10000ms
): Promise<AspectRatioResult | null> => {
  const firstVideoItem = items.find(item => item.type === 'video');
  const firstImageItem = items.find(item => item.type === 'image');

  if (firstVideoItem) {
    return new Promise<AspectRatioResult | null>((resolve) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.preload = 'metadata';

      const timeoutId = setTimeout(() => {
        cleanup();
        resolve(null);
      }, timeout);

      const cleanup = () => {
        clearTimeout(timeoutId);
        video.removeEventListener('loadedmetadata', onLoad);
        video.removeEventListener('error', onError);
        video.src = '';
      };

      const onLoad = () => {
        const ratio = video.videoWidth / video.videoHeight;
        console.log('🎬 [AspectRatioDetector] Video metadata loaded:', {
          width: video.videoWidth,
          height: video.videoHeight,
          ratio: ratio,
          url: video.src.substring(0, 100)
        });
        const result: AspectRatioResult = {
          ratio: classifyAspectRatio(ratio),
          confidence: 'medium',
          dimensions: { 
            width: video.videoWidth, 
            height: video.videoHeight 
          },
          source: 'metadata'
        };
        cleanup();
        resolve(result);
      };

      const onError = () => {
        console.error('❌ [AspectRatioDetector] Video metadata load failed for:', video.src.substring(0, 100));
        cleanup();
        resolve(null);
      };

      video.addEventListener('loadedmetadata', onLoad);
      video.addEventListener('error', onError);
      video.src = firstVideoItem.url;
    });
  }

  if (firstImageItem) {
    return new Promise<AspectRatioResult | null>((resolve) => {
      const img = document.createElement('img');
      // Remove crossOrigin to avoid CORS issues
      // img.crossOrigin = 'anonymous';

      const timeoutId = setTimeout(() => {
        cleanup();
        resolve(null);
      }, timeout);

      const cleanup = () => {
        clearTimeout(timeoutId);
        img.removeEventListener('load', onLoad);
        img.removeEventListener('error', onError);
        img.src = '';
      };

      const onLoad = () => {
        const ratio = img.naturalWidth / img.naturalHeight;
        console.log('🔍 [AspectRatioDetector] Image loaded - dimensions:', img.naturalWidth, 'x', img.naturalHeight, 'ratio:', ratio);
        const result: AspectRatioResult = {
          ratio: classifyAspectRatio(ratio),
          confidence: 'high',
          dimensions: { 
            width: img.naturalWidth, 
            height: img.naturalHeight 
          },
          source: 'metadata'
        };
        cleanup();
        resolve(result);
      };

      const onError = (error: any) => {
        console.log('🔍 [AspectRatioDetector] Image load error:', error);
        cleanup();
        resolve(null);
      };

      img.addEventListener('load', onLoad);
      img.addEventListener('error', onError);
      img.src = firstImageItem.url;
    });
  }

  return null;
};

/**
 * Main aspect ratio detection function
 * Tries multiple detection methods with fallback
 * Optimized to avoid loading states for instant detections
 */
export const detectAspectRatio = async (
  items: MediaItem[],
  options: DetectionOptions = {}
): Promise<AspectRatioResult> => {
  const {
    preferExistingElements = true,
    timeout = 5000,
    fallbackRatio = '16:9'
  } = options;

  // Only log when actually processing new detection
  const shouldLog = items.length > 0;
  if (shouldLog) {
    console.log('🔍 [AspectRatioDetector] Starting detection with', items.length, 'items');
  }

  // Early return for empty items
  if (!items || items.length === 0) {
    if (shouldLog) {
      console.log('🔍 [AspectRatioDetector] No items found, using fallback');
    }
    return {
      ratio: fallbackRatio,
      confidence: 'low',
      source: 'fallback'
    };
  }

  try {
    // Method 1: Check existing DOM elements (fast and synchronous)
    // DISABLED: Force detection from S3 URL instead of DOM elements
    // if (preferExistingElements) {
    //   console.log('🔍 [AspectRatioDetector] Trying Method 1: Existing DOM elements...');
    //   const existingResult = await detectFromExistingElements(items);
    //   if (existingResult) {
    //     if (shouldLog) {
    //       console.log('🔍 [AspectRatioDetector] ✅ Instant detection from existing element:', existingResult);
    //     }
    //     return existingResult;
    //   }
    //   console.log('🔍 [AspectRatioDetector] ❌ Method 1 failed: No suitable DOM elements found');
    // }

    // Method 2: Load metadata from S3 (slower but accurate)
    console.log('🔍 [AspectRatioDetector] Trying Method 2: Loading metadata...');
    const metadataResult = await detectFromMetadata(items, timeout);
    if (metadataResult) {
      if (shouldLog) {
        console.log('🔍 [AspectRatioDetector] ✅ Detected from metadata:', metadataResult);
      }
      return metadataResult;
    }
    console.log('🔍 [AspectRatioDetector] ❌ Method 2 failed: Could not load metadata');

    // Method 3: Fallback
    console.log('🔍 [AspectRatioDetector] Using Method 3: Fallback ratio:', fallbackRatio);
    return {
      ratio: fallbackRatio,
      confidence: 'low',
      source: 'fallback'
    };

  } catch (error) {
    console.error('🔍 [AspectRatioDetector] Detection error:', error);
    return {
      ratio: fallbackRatio,
      confidence: 'low',
      source: 'fallback'
    };
  }
};

/**
 * Helper function to get display info for aspect ratio
 */
export const getAspectRatioDisplay = (ratio: AspectRatioType) => {
  const displays = {
    '16:9': { emoji: '📺', label: 'Horizontal', description: 'Widescreen' },
    '9:16': { emoji: '📱', label: 'Vertical', description: 'Mobile Portrait' },
    '1:1': { emoji: '⬜', label: 'Square', description: 'Instagram Square' }
  };
  
  return displays[ratio];
};

/**
 * React hook for aspect ratio detection
 */
export const useAspectRatioDetector = () => {
  const detect = async (
    items: MediaItem[],
    options?: DetectionOptions
  ): Promise<AspectRatioResult> => {
    return detectAspectRatio(items, options);
  };

  return { detect };
};
